import React from "react";
import { Styled } from "./styles";
import { useNavigate, useParams } from "react-router-dom";
import useQueryProducts from "@/hooks/queries/useQueryProducts";
import { getRoutes } from "@/utils/helpers";
import { Box, Button, Typography } from "@mui/material";
import { isDefined } from "@/utils/validations";
import { withErrorBoundary } from "@/components/hoc";
import { Logger, Params } from "@/types/utils";
import { DistilledAlcohol, Food, Furniture, OtherBeverages, Wine } from "@dippi/forms";
import { log } from "@/utils/logger";
import { Product, Subscription } from "@/types/entities";
import { Sidebar } from "@/components/product";
import { classificationCodeToName } from "@/utils/classification/names";
import api from "@/config/api";
import type { HttpClient } from "@narisolutions/api-client";
import useQuerySubscription from "@/hooks/queries/useQuerySubscription";

const Page: React.FC = () => {
    const { pid, typeName } = useParams<Params>();
    const { data } = useQueryProducts();

    const navigate = useNavigate();
    const routes = getRoutes();

    const product = pid && data ? data.find(i => i._id === pid) : undefined;
    const productType = Object.entries(classificationCodeToName).find(i => i[0] === typeName)?.[1]
        .title;

    const { data: subscription } = useQuerySubscription();

    if (!subscription) return <Styled.Container />;

    const usage = subscription.usage;

    if (!pid && isDefined(usage.labels.available) && usage.labels.available === 0) {
        return (
            <Styled.Container>
                <Typography>
                    You've used up all available digital labels for your current {subscription.plan}
                    plan.
                </Typography>
                <Button
                    sx={{ margin: "20px 0px" }}
                    onClick={() => navigate(routes.organizationSettings())}
                >
                    Upgrade
                </Button>
            </Styled.Container>
        );
    }

    const Form = forms[typeName!];

    return (
        <Box display="flex">
            <Sidebar title={product ? product.shortTitle : (productType as string)} />
            <Form subscription={subscription} product={product} log={log} api={api} />
        </Box>
    );
};

const forms: Record<
    Product["typeName"],
    React.FC<{
        subscription: Subscription;
        product: Product | undefined;
        log: Logger;
        api: HttpClient;
    }>
> = {
    "10.31.14": Food,
    "10.39.18": Food,
    "10.39.22": Food,
    "10.84.12": Food,
    "11.01.10": DistilledAlcohol,
    "11.02.11": Wine,
    "11.02.12": Wine,
    "11.07.19": OtherBeverages,
    "31.09.1": Furniture,
};

export default withErrorBoundary({ component: "Product" })(Page);
